import logger from '../../utils/logger';

/**
 * Manages Firebase subscriptions to ensure proper cleanup on logout
 */
class SubscriptionManager {
  private subscriptions: Map<string, () => void> = new Map();

  /**
   * Add a subscription to be managed
   * @param key Unique key for the subscription
   * @param unsubscribe Function to call to unsubscribe
   */
  add(key: string, unsubscribe: () => void) {
    // If there's already a subscription with this key, unsubscribe it first
    if (this.subscriptions.has(key)) {
      logger.warn(`Subscription with key ${key} already exists, replacing it`);
      this.remove(key);
    }
    
    this.subscriptions.set(key, unsubscribe);
    logger.debug(`Added subscription: ${key}`);
  }

  /**
   * Remove and unsubscribe a specific subscription
   * @param key Key of the subscription to remove
   */
  remove(key: string) {
    const unsubscribe = this.subscriptions.get(key);
    if (unsubscribe) {
      try {
        unsubscribe();
        this.subscriptions.delete(key);
        logger.debug(`Removed subscription: ${key}`);
      } catch (error) {
        logger.error(`Error unsubscribing ${key}:`, error);
      }
    }
  }

  /**
   * Clear all subscriptions
   * Should be called on logout
   */
  clearAll() {
    logger.info(`Clearing ${this.subscriptions.size} Firebase subscriptions`);
    
    this.subscriptions.forEach((unsubscribe, key) => {
      try {
        unsubscribe();
        logger.debug(`Unsubscribed: ${key}`);
      } catch (error) {
        logger.error(`Error unsubscribing ${key}:`, error);
      }
    });
    
    this.subscriptions.clear();
  }

  /**
   * Get the current number of active subscriptions
   */
  get count(): number {
    return this.subscriptions.size;
  }

  /**
   * List all active subscription keys (for debugging)
   */
  listActive(): string[] {
    return Array.from(this.subscriptions.keys());
  }
}

// Export singleton instance
export const subscriptionManager = new SubscriptionManager();

// Export for use in window for debugging
if (typeof window !== 'undefined') {
  (window as any).subscriptionManager = subscriptionManager;
}